import React, { useState } from 'react'
import { Gamepad2, BarChart3 } from 'lucide-react'
import { useTranslation } from '../hooks/use-i18n'
import { formatPoints } from '../utils/points-label'
import { Formula1PlayerPoints } from '../types/formula1'
import ViewPlayerFormula1StatsModal from './ViewPlayerFormula1StatsModal'

const rankBadge = (rank: number) => {
  if (rank === 1) return '🥇'
  if (rank === 2) return '🥈'
  if (rank === 3) return '🥉'
  return null
}

interface Formula1PlayerItemProps {
  player: Formula1PlayerPoints
  index: number
  roomId: string
}

const Formula1PlayerItem: React.FC<Formula1PlayerItemProps> = ({ player, index, roomId }) => {
  const { t } = useTranslation()
  const [isOpenGamesModal, setIsOpenGamesModal] = useState(false)
  const [isOpenStatisticsModal, setIsOpenStatisticsModal] = useState(false)

  const playerData = {
    _id: player.playerId,
    id: player.playerId,
    name: player.player?.name || 'Unknown Player',
    email: player.player?.email || '',
    avatar: player.player?.avatar,
  }

  const isTop3 = index < 3
  const colors = ['bg-primary', 'bg-destructive', 'bg-success', 'bg-warning', 'bg-chess', 'bg-primary', 'bg-destructive', 'bg-success']
  const color = colors[index % colors.length]

  return (
    <>
      <div
        className={`rounded-lg sm:rounded-xl p-2.5 sm:p-3.5 lg:p-4 flex items-center gap-2 sm:gap-3 lg:gap-4 transition-all hover:bg-muted/30 ${
          isTop3 ? 'glass-card border border-primary/10' : 'glass-card'
        }`}
      >
        <span className="text-base sm:text-display-2 font-display font-bold w-6 sm:w-7 lg:w-8 text-center text-muted-foreground shrink-0">
          {rankBadge(index + 1) || (index + 1)}
        </span>
        <div
          className={`max-[479px]:hidden w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full ${color} flex items-center justify-center text-[0.65rem] sm:text-small font-bold text-primary-foreground shrink-0`}
        >
          {playerData.name.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <button
            type="button"
            onClick={() => setIsOpenGamesModal(true)}
            className="font-semibold text-small sm:text-nav truncate text-foreground hover:text-primary transition-colors text-left max-w-full"
          >
            {playerData.name}
          </button>
          <div className="flex items-center gap-2 sm:gap-3 mt-0.5">
            <span className="text-small sm:text-nav font-semibold text-primary">
              {formatPoints(player.totalPoints, t)}
            </span>
            <span className="text-small text-muted-foreground/60">·</span>
            <span className="text-small text-muted-foreground">
              {player.racesParticipated} races
            </span>
            <span className="text-small text-muted-foreground/60">·</span>
            <span className="text-small text-muted-foreground">
              {player.correctPredictions} correct
            </span>
          </div>
        </div>
        <div className="flex gap-0.5 sm:gap-1 lg:gap-1.5 shrink-0">
          <button
            onClick={() => setIsOpenGamesModal(true)}
            className="p-1.5 sm:px-2 sm:py-1.5 rounded-md sm:rounded-lg text-sky-400 text-small font-medium hover:opacity-80 transition-opacity flex items-center gap-1"
            aria-label={t('roomPage.games')}
          >
            <Gamepad2 size={14} className="shrink-0" /> <span className="hidden sm:inline">{t('roomPage.games')}</span>
          </button>
          <button
            onClick={() => setIsOpenStatisticsModal(true)}
            className="p-1.5 sm:px-2 sm:py-1.5 rounded-md sm:rounded-lg text-violet-400 text-small font-medium hover:opacity-80 transition-opacity flex items-center gap-1"
            aria-label={t('roomPage.stats')}
          >
            <BarChart3 size={14} className="shrink-0" /> <span className="hidden sm:inline">{t('roomPage.stats')}</span>
          </button>
        </div>
      </div>

      {isOpenGamesModal && (
        <ViewPlayerFormula1StatsModal
          isOpen={isOpenGamesModal}
          player={playerData}
          playerId={player.playerId}
          roomId={roomId}
          viewType="games"
          onClose={() => setIsOpenGamesModal(false)}
        />
      )}

      {isOpenStatisticsModal && (
        <ViewPlayerFormula1StatsModal
          isOpen={isOpenStatisticsModal}
          player={playerData}
          playerId={player.playerId}
          roomId={roomId}
          viewType="stats"
          onClose={() => setIsOpenStatisticsModal(false)}
        />
      )}
    </>
  )
}

export default Formula1PlayerItem
